import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { mediaUrl } from "@/lib/api";
import { Trash2, Minus, Plus, ArrowLeft, ShoppingCart } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Cart() {
  const { items, removeItem, updateQty, subtotal, count } = useCart();
  const navigate = useNavigate();

  return (
    <div data-testid="cart-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
      <Link to="/shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white">
        <ArrowLeft size={14} /> Continue shopping
      </Link>

      <div className="bg-sand/90 backdrop-blur-sm border border-tan/50 p-8 lg:p-10 float-card mt-6">
        <span className="eyebrow">Your Cart</span>
        <h1 className="font-serif text-4xl text-navy mt-2">Review your order</h1>

        {items.length === 0 ? (
          <div className="mt-10 text-center py-12">
            <ShoppingCart size={36} className="mx-auto text-charcoal/40" strokeWidth={1.25} />
            <p className="text-charcoal/75 mt-4">Your cart is empty.</p>
            <Link to="/shop" data-testid="cart-empty-shop-btn" className="mt-6 inline-block bg-navy text-white px-7 py-3 text-xs uppercase tracking-[0.2em] hover:bg-charcoal">
              Browse the catalog
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-8 divide-y divide-tan/40 border-t border-tan/40">
              {items.map((it) => (
                <div key={it.key} data-testid={`cart-item-${it.slug}`} className="py-5 flex items-center gap-4">
                  <div className="w-20 h-20 bg-white border border-tan/40 overflow-hidden flex-shrink-0">
                    {it.image_url && <img src={mediaUrl(it.image_url)} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/shop/${it.slug}`} className="font-serif text-xl text-navy leading-tight hover:text-copper">{it.name}</Link>
                    {it.variant_label && <div className="text-xs uppercase tracking-[0.15em] text-charcoal/70 mt-0.5">{it.variant_label}</div>}
                    <div className="text-sm text-copper mt-1">{money(it.unit_price)} each</div>
                  </div>
                  <div className="flex items-center border border-tan/50 bg-white">
                    <button data-testid={`cart-dec-${it.slug}`} onClick={() => updateQty(it.key, it.qty - 1)} className="px-2.5 py-2 text-navy hover:bg-sand"><Minus size={14} /></button>
                    <span className="px-3 text-sm text-navy w-9 text-center">{it.qty}</span>
                    <button data-testid={`cart-inc-${it.slug}`} onClick={() => updateQty(it.key, it.qty + 1)} className="px-2.5 py-2 text-navy hover:bg-sand"><Plus size={14} /></button>
                  </div>
                  <div className="w-24 text-right font-medium text-navy">{money(it.unit_price * it.qty)}</div>
                  <button data-testid={`cart-remove-${it.slug}`} onClick={() => removeItem(it.key)} className="text-charcoal/50 hover:text-texasred p-1"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-between items-center border-t border-tan/40 pt-5">
              <span className="text-sm uppercase tracking-[0.2em] text-charcoal/70">Subtotal ({count} item{count !== 1 ? "s" : ""})</span>
              <span data-testid="cart-subtotal" className="font-serif text-3xl text-navy">{money(subtotal)}</span>
            </div>
            <p className="text-[11px] text-charcoal/60 mt-2">Taxes calculated at checkout. Pay online or at pickup / delivery.</p>

            <button data-testid="cart-checkout-btn" onClick={() => navigate("/checkout")} className="mt-7 w-full bg-navy text-white px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-charcoal">
              Proceed to Checkout
            </button>
          </>
        )}
      </div>
    </div>
  );
}
